(() => {
  const selector = '[data-video-grid]'

  function initVideoGrid(root) {
    if (!root || root.dataset.videoGridInitialized === 'true') return

    root.dataset.videoGridInitialized = 'true'

    const groups = Array.from(root.querySelectorAll('[data-video-grid-group]'))
    const tablist = root.querySelector('[data-video-grid-tabs]')
    const modal = root.querySelector('[data-video-grid-modal]')
    const modalTitle = modal?.querySelector('[data-video-grid-modal-title]')
    const iframe = modal?.querySelector('[data-video-grid-iframe]')
    const upload = modal?.querySelector('[data-video-grid-upload]')

    let returnFocus = null

    function currentHash() {
      const hash = window.location.hash.slice(1).toLowerCase()

      try {
        return decodeURIComponent(hash)
      } catch (error) {
        return hash
      }
    }

    function videoCards() {
      return Array.from(root.querySelectorAll('[data-video-grid-card][data-video-hash]'))
    }

    function cardFromHash() {
      const hash = currentHash()
      if (!hash) return null

      return videoCards().find((card) => card.dataset.videoHash === hash) || null
    }

    function updateHash(hash, replace = false) {
      const url = new URL(window.location.href)
      url.hash = hash ? `#${hash}` : ''
      window.history[replace ? 'replaceState' : 'pushState']({}, '', `${url.pathname}${url.search}${url.hash}`)
    }

    function tabs() {
      return Array.from(tablist?.querySelectorAll('[role="tab"]') || [])
    }

    function showAllGroups(focusIndex = 0, focusTab = false) {
      const availableTabs = tabs()

      availableTabs.forEach((tab, index) => {
        tab.setAttribute('aria-selected', 'false')
        tab.tabIndex = index === focusIndex ? 0 : -1
      })

      groups.forEach((group) => {
        group.hidden = false
      })

      root.dataset.videoGridView = 'all'

      if (focusTab) {
        availableTabs[focusIndex]?.focus()
      }
    }

    function activateTab(index, focusTab = false) {
      const availableTabs = tabs()

      availableTabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index

        tab.setAttribute('aria-selected', String(active))
        tab.tabIndex = active ? 0 : -1

        if (groups[tabIndex]) {
          groups[tabIndex].hidden = !active
        }
      })

      root.dataset.videoGridView = 'single'

      if (focusTab) {
        availableTabs[index]?.focus()
      }
    }

    root.dataset.videoGridView = 'all'

    // Always create/show the tablist when it exists.
    // Default state has no selected tab and all groups visible.
    if (tablist && groups.length > 0) {
      // Prevent duplicate tabs if the section is reinitialized.
      tablist.innerHTML = ''

      groups.forEach((group, index) => {
        const tabId = `VideoGridTab-${root.dataset.videoGridId}-${index}`
        const panelId = `VideoGridPanel-${root.dataset.videoGridId}-${index}`
        const tab = document.createElement('button')

        tab.type = 'button'
        tab.className = 'video-grid__tab'
        tab.id = tabId
        tab.setAttribute('role', 'tab')
        tab.setAttribute('aria-controls', panelId)
        tab.setAttribute('aria-selected', 'false')
        tab.tabIndex = index === 0 ? 0 : -1
        tab.textContent =
          group.dataset.videoGridGroupTitle || `Group ${index + 1}`

        tab.addEventListener('click', () => {
          const isSelected =
            tab.getAttribute('aria-selected') === 'true'

          if (isSelected) {
            showAllGroups(index)
          } else {
            activateTab(index)
          }
        })

        tab.addEventListener('keydown', (event) => {
          let nextIndex = index

          if (event.key === 'ArrowRight') {
            nextIndex = (index + 1) % groups.length
          } else if (event.key === 'ArrowLeft') {
            nextIndex = (index - 1 + groups.length) % groups.length
          } else if (event.key === 'Home') {
            nextIndex = 0
          } else if (event.key === 'End') {
            nextIndex = groups.length - 1
          } else if (event.key === 'Escape') {
            event.preventDefault()
            showAllGroups(index, true)
            return
          } else {
            return
          }

          event.preventDefault()
          activateTab(nextIndex, true)
        })

        group.id = panelId
        group.setAttribute('role', 'tabpanel')
        group.setAttribute('aria-labelledby', tabId)
        group.tabIndex = 0

        tablist.append(tab)
      })

      tablist.hidden = false

      // Initial state:
      // - tablist visible
      // - no tab selected
      // - all groups visible
      showAllGroups()
    } else {
      groups.forEach((group) => {
        group.hidden = false
      })
    }

    function resetPlayer() {
      if (iframe) {
        iframe.src = ''
        iframe.hidden = true
      }

      if (upload) {
        upload.pause()
        upload.removeAttribute('src')
        upload.load()
        upload.hidden = true
      }
    }

    function openModal(card, updateUrl = true) {
      const source = card?.dataset.videoSrc

      if (!source || !modal) return

      returnFocus = card

      if (modalTitle) {
        modalTitle.textContent = card.dataset.videoTitle || 'Video'
      }

      resetPlayer()

      if (card.dataset.videoType === 'upload' && upload) {
        upload.src = source
        upload.hidden = false
        upload.play().catch(() => {})
      } else if (iframe) {
        iframe.src = source
        iframe.hidden = false
      }

      if (!modal.open) {
        modal.showModal()
      }

      if (updateUrl && card.dataset.videoHash !== currentHash()) {
        updateHash(card.dataset.videoHash)
      }
    }

    function closeModal(updateUrl = true) {
      if (!modal) return

      const activeVideoCard = cardFromHash()

      resetPlayer()

      if (modal.open) {
        modal.close()
      }

      returnFocus?.focus({
        preventScroll: true,
      })

      returnFocus = null

      if (updateUrl && activeVideoCard) {
        updateHash('', true)
      }
    }

    root.addEventListener('click', (event) => {
      const card = event.target.closest('[data-video-grid-card]')

      if (card && root.contains(card)) {
        openModal(card)
        return
      }

      if (event.target.closest('[data-video-grid-close]')) {
        closeModal()
      }
    })

    modal?.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal()
      }
    })

    modal?.addEventListener('close', resetPlayer)

    modal?.addEventListener('cancel', (event) => {
      event.preventDefault()
      closeModal()
    })

    function syncModalWithHash() {
      const card = cardFromHash()

      if (card) {
        openModal(card, false)
      } else if (modal?.open) {
        closeModal(false)
      }
    }

    window.addEventListener('hashchange', syncModalWithHash)
    syncModalWithHash()
  }

  function initWithin(scope = document) {
    if (scope.matches?.(selector)) {
      initVideoGrid(scope)
    }

    scope.querySelectorAll?.(selector).forEach(initVideoGrid)
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initWithin(),
      { once: true }
    )
  } else {
    initWithin()
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(event.target)
    }
  )
})()
